import { useMemo, useState } from 'react';
import './DailyCaloric.css';
import { commitNumberInput, clamp, handleArrowCommit, handleRangeIntChange } from './helpers.js';
import FitFacts from './FitFacts.js';

const ACTIVITY_LEVELS = [
	{
		id: 'option-activity-level-1',
		value: 1.2,
		title: 'Little or no exercise',
		image: '/images/webcontent/assets/education-resources/lifestyle/acefit/5step-1.png'
	},
	{
		id: 'option-activity-level-2',
		value: 1.375,
		title: 'Light Exercise or sports 1-3 days a week',
		image: '/images/webcontent/assets/education-resources/lifestyle/acefit/5step-2.png'
	},
	{
		id: 'option-activity-level-3',
		value: 1.55,
		title: 'Medical Exercise or sports 3-5 days a week',
		image: '/images/webcontent/assets/education-resources/lifestyle/acefit/5step-3.png'
	},
	{
		id: 'option-activity-level-4',
		value: 1.725,
		title: 'Hard Exercise, or sports 6-7 days a week',
		image: '/images/webcontent/assets/education-resources/lifestyle/acefit/5step-4.png'
	},
	{
		id: 'option-activity-level-5',
		value: 1.9,
		title: 'Hard Exercise or sports 6-7 days a week',
		image: '/images/webcontent/assets/education-resources/lifestyle/acefit/5step-5.png'
	}
];

const calculateCaloricNeeds = ({ age, weight, gender, feet, inches, activityLevel }) => {
	const heightInches = parseFloat((feet * 12) + inches);
	const weightKg = parseFloat(weight) * 0.45359237;
	const heightCm = parseFloat(heightInches) * 2.54;
	const ageValue = parseFloat(age);

	let caloricResults = 0;

	if (gender === 'Male') {
		caloricResults = ((9.99 * weightKg) + (6.25 * heightCm) - (4.92 * ageValue) + 5) * parseFloat(activityLevel);
	}
	else {
		caloricResults = ((9.99 * weightKg) + (6.25 * heightCm) - (4.92 * ageValue) - 161) * parseFloat(activityLevel);
	}

	return caloricResults.toFixed(2).toString();
};

export default function DailyCaloric() {
	const [age, setAge] = useState(35);
	const [weight, setWeight] = useState(169);
	const [gender, setGender] = useState('Male');
	const [feet, setFeet] = useState(5);
	const [inches, setInches] = useState(7);
	const [activityLevel, setActivityLevel] = useState(1.55);
	const [ageInput, setAgeInput] = useState('35');
	const [weightInput, setWeightInput] = useState('169');
	const [feetInput, setFeetInput] = useState('5');
	const [inchesInput, setInchesInput] = useState('7');

	const caloricNeeds = useMemo(
		() => calculateCaloricNeeds({ age, weight, gender, feet, inches, activityLevel }),
		[age, weight, gender, feet, inches, activityLevel]
	);

	return (
		<div className="calc-container-scroller clearfix spc-m-b-2">
			<div className="calc-container-scroller-inner">
				<div className="calculator-wrap">
					<h3>Calculate Your Daily Caloric Needs</h3>

					<div className="calculator-row">
						<div className="range-row">
							<div className="range-row__input-wrap">
								<input
									id="range-age"
									type="range"
									min="18"
									max="100"
									value={age}
									onChange={(e) => handleRangeIntChange(e, setAge, setAgeInput)}
								/>
							</div>

							<div className="range-row__result-wrap">
								<label htmlFor="result-age">Age: </label>
								<input
									type="number"
									id="result-age"
									name="result-age"
									value={ageInput}
									onChange={(e) => setAgeInput(e.target.value)}
									onKeyUp={(e) => handleArrowCommit(e, 18, 100, age, setAge, setAgeInput)}
									onBlur={() => commitNumberInput(ageInput, 18, 100, age, setAge, setAgeInput)}
								/>
							</div>
						</div>
					</div>

					<div className="calculator-row">
						<div className="range-row">
							<div className="range-row__input-wrap">
								<input
									id="range-weight"
									type="range"
									min="50"
									max="300"
									value={weight}
									onChange={(e) => handleRangeIntChange(e, setWeight, setWeightInput)}
								/>
							</div>

							<div className="range-row__result-wrap">
								<label htmlFor="result-weight">Weight: </label>
								<input
									type="number"
									id="result-weight"

									name="result-weight"
									value={weightInput}
									onChange={(e) => setWeightInput(e.target.value)}
									onKeyUp={(e) => handleArrowCommit(e, 50, 300, weight, setWeight, setWeightInput)}
									onBlur={() => commitNumberInput(weightInput, 50, 300, weight, setWeight, setWeightInput)}
								/>
							</div>
						</div>
					</div>

					<div className="calculator-row">
						<div className="radio-row">
							<div className="radio-row__input-wrap">
								<label className={`radio-label radio-img-mask ${gender === 'Male' ? 'active' : ''}`} htmlFor="option-gender-male">
									<input
										id="option-gender-male"
										name="option-gender"
										type="radio"
										value="Male"
										checked={gender === 'Male'}
										onChange={(e) => setGender(e.target.value)}
									/>
									<img src="/images/webcontent/assets/education-resources/lifestyle/acefit/iconMaleSmall.png" style={{ height: '30px' }} alt="Male" />
								</label>

								<label className={`radio-label radio-img-mask ${gender === 'Female' ? 'active' : ''}`} htmlFor="option-gender-female">
									<input
										id="option-gender-female"
										name="option-gender"
										type="radio"
										value="Female"
										checked={gender === 'Female'}
										onChange={(e) => setGender(e.target.value)}
									/>
									<img src="/images/webcontent/assets/education-resources/lifestyle/acefit/iconFemaleSmall.png" style={{ height: '30px' }} alt="Female" />
								</label>
							</div>

							<div className="radio-row__result-wrap">
								<label htmlFor="result-gender">Gender: </label>
								<input type="text" id="result-gender" name="result-gender" readOnly value={gender} />
							</div>
						</div>
					</div>

					<div className="calculator-row">
						<div className="range-row">
							<div className="range-row__input-wrap">
								<input
									id="range-feet"
									type="range"
									min="3"
									max="8"
									value={feet}
									onChange={(e) => handleRangeIntChange(e, setFeet, setFeetInput)}
								/>
							</div>

							<div className="range-row__result-wrap">
								<label htmlFor="result-feet">Feet: </label>
								<input
									type="number"
									id="result-feet"
									name="result-feet"
									value={feetInput}
									onChange={(e) => setFeetInput(e.target.value)}
									onKeyUp={(e) => handleArrowCommit(e, 3, 8, feet, setFeet, setFeetInput)}
									onBlur={() => commitNumberInput(feetInput, 3, 8, feet, setFeet, setFeetInput)}
								/>
							</div>
						</div>
					</div>

					<div className="calculator-row">
						<div className="range-row">
							<div className="range-row__input-wrap">
								<input
									id="range-inches"
									type="range"
									min="0"
									max="11"
									value={inches}
									onChange={(e) => handleRangeIntChange(e, setInches, setInchesInput)}
								/>
							</div>

							<div className="range-row__result-wrap">
								<label htmlFor="result-inches">Inches: </label>
								<input
									type="number"
									id="result-inches"
									name="result-inches"
									value={inchesInput}
									onChange={(e) => setInchesInput(e.target.value)}
									onKeyUp={(e) => handleArrowCommit(e, 0, 11, inches, setInches, setInchesInput)}
									onBlur={() => commitNumberInput(inchesInput, 0, 11, inches, setInches, setInchesInput)}
								/>
							</div>
						</div>
					</div>

					<div className="calculator-row">
						<h4 className="calculator-row-title spc-m-b-half">Select Your Activity Level:</h4>

						<div className="radio-row">
							<div className="radio-row__input-wrap">
								{ACTIVITY_LEVELS.map((option) => (
									<label
										key={option.id}
										className={`radio-label radio-img-mask ${activityLevel === option.value ? 'active' : ''}`}
										htmlFor={option.id}
									>
										<input
											id={option.id}
											name="option-activity-level"
											type="radio"
											value={option.value}
											checked={activityLevel === option.value}
											onChange={(e) => setActivityLevel(parseFloat(e.target.value))}
										/>
										<img title={option.title} alt={option.title} src={option.image} style={{ width: '70px' }} />
									</label>
								))}
							</div>

							<div className="radio-row__result-wrap hidden">
								<input type="hidden" id="result-activity-level" name="result-activity-level" readOnly value={activityLevel.toFixed(3)} />
							</div>
						</div>
					</div>

					<hr />

					<div className="results-row">
						<h4 className="results-row__label">Your Estimated Daily Caloric Need is:</h4>

						<input
							type="text"
							className="results-row__value"
							readOnly
							id="result-caloric-needs"
							name="result-caloric-needs"
							value={caloricNeeds}
						/>
					</div>

                    <FitFacts
                        items={[
                            {
                                label: '8 Diet Myths Debunked',
                                link: '/images/webcontent/assets/education-resources/lifestyle/fitfacts/pdfs/fitfacts/itemid_3566.pdf'
                            },
                            {
                                label: 'Blood Sugar and Hemoglobin A1C',
                                link: '/images/webcontent/assets/education-resources/lifestyle/fitfacts/pdfs/fitfacts/itemid_3593.pdf'
                            }
                        ]}
                    />
				</div>
			</div>
		</div>
	);
}
